import { useState } from 'react'
import { toast } from 'react-toastify'
import axios from 'axios'
import LoadingSpinner from '../common/LoadingSpinner'

const RemoveGymModal = ({ isOpen, onClose, onSuccess, gym }) => {
  const [loading, setLoading] = useState(false)
  const [note, setNote] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!note.trim()) {
      toast.error('Please provide a reason for removing this gym')
      return
    }

    if (note.trim().length < 10) {
      toast.error('Please provide a more detailed reason (at least 10 characters)')
      return
    }

    setLoading(true)

    try {
      const response = await axios.delete(`/api/gyms/${gym.id}`, {
        data: {
          reason: note.trim(),
          moveToExpired: true
        }
      })

      toast.success(response.data.message || 'Gym removed successfully')
      onSuccess(response.data)
      handleClose()
    } catch (error) {
      console.error('Error removing gym:', error)
      const message = error.response?.data?.error || 'Failed to remove gym'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setNote('')
    onClose()
  }

  if (!isOpen || !gym) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Remove Gym
                </h3>
                <p className="text-sm text-gray-500">
                  This action will move the gym to expired clients
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4">
          {/* Gym Info */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">{gym.name}</h4>
                <p className="text-sm text-gray-500">{gym.city || gym.location}</p>
                <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-700 rounded">
                  {gym.stage}
                </span>
              </div>
            </div>
          </div>

          {/* Warning */}
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex">
              <svg className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Are you sure you want to remove this gym?</p>
                <p className="mt-1">The gym will be moved to the expired clients list and can be viewed in the reports section.</p>
              </div>
            </div>
          </div>

          {/* Reason Input */}
          <div className="space-y-2">
            <label htmlFor="removeReason" className="block text-sm font-medium text-gray-700">
              Reason for Removal <span className="text-red-500">*</span>
            </label>
            <textarea
              id="removeReason"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="Please explain why this gym is being removed (e.g., Owner not interested, Unable to reach agreement, Location not suitable, etc.)"
              required
            />
            <p className="text-xs text-gray-500">
              {note.length}/10 characters minimum
            </p>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || note.trim().length < 10}
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Removing...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Remove Gym
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default RemoveGymModal
